import { GetServerSidePropsContext } from 'next';
import useTranslation from 'next-translate/useTranslation';
import { useContext, useEffect } from 'react';

import {
  PAGE_TYPE,
  Referrer,
} from '@invideoio/web-shared/utils/utils.constant';

import CategoryTemplateContent from '@components/category-templates/CategoryTemplateContent';
import CategoryTemplateHead from '@components/category-templates/CategoryTemplateHead';
import CategoryTemplateLeftPanel from '@components/category-templates/CategoryTemplateLeftPanel';
import CategoryTemplates from '@components/category-templates/CategoryTemplates';
import HomepageSEO from '@components/common/SEO/HomepageSEO';
import HeaderWithCTA from '@components/header-with-cta';
import { footerExploreLinks } from '@constants/footerExplore';
import { TEMPLATE_LIMIT } from '@constants/templates';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import Footer from '@invideoio/web-shared/components/Footer';
import {
  TemplateSearchData,
  TemplateSearchPayload,
  TemplateSearchResponse,
} from '@invideoio/web-shared/types/Templates';
import { AnalyticsUtils } from '@invideoio/web-shared/utils/analytics/AnalyticsUtils';
import AppLayout from '@layouts/AppLayout';
import {
  fetchAllTemplateCategories,
  searchTemplates,
} from '@lib/API/template.api';
import { StateContext } from '@store/store';
import { AmplitudeEvents, sendAmplitudeAnalyticEvent } from '@utils/analytics';
import { parseFiltersFromQueryParams } from '@utils/template-category/filter.utils';
import { processTemplateSearchAPIResponse } from '@utils/template-category/template.utils';
import { BrowserType } from '@invideoio/web-shared/constants/common';
import BannerSlot from '@components/banner-slot';

interface TemplateCategoryPageProps {
  templateList: TemplateSearchResponse;
  filters?: any;
  templatePageData: any;
  templateCategories: any[];
  currentPage: number;
  browser: BrowserType;
}

function TemplateCategory(props: TemplateCategoryPageProps) {
  const {
    templateList,
    filters,
    templatePageData,
    templateCategories,
    currentPage,
    browser,
  } = props;

  const globalState = useContext(StateContext);
  const { t, lang } = useTranslation('home');
  const { platform, os, config } = globalState;

  useEffect(() => {
    const AppName = process.env.NEXT_PUBLIC_APP_NAME;

    if (AppName === 'kizoa') {
      AnalyticsUtils.setReferrer(Referrer.KIZOA);
    } else {
      AnalyticsUtils.setReferrer(Referrer.WEBSITE);
    }

    AnalyticsUtils.registerPage(PAGE_TYPE.TEMPLATE_CATEGORY as any, {
      location: window.location.href,
    });

    sendAmplitudeAnalyticEvent(AmplitudeEvents.appLoad);
  }, []);

  return (
    <AppLayout platform={platform}>
      <HomepageSEO seo={templatePageData.SEO} />
      <BannerSlot browser={browser} />
      <HeaderWithCTA />

      <CategoryTemplateHead Head={templatePageData.Head} />

      <div
        className={ivclass(
          'iv-relative iv-overflow-x-hidden',
          'iv-flex md:iv-flex-col sm:iv-flex-col',
          'iv-px-40 sm:iv-px-25',
        )}
      >
        <CategoryTemplateLeftPanel
          templateCategories={templateCategories}
          templateCategoryName={templatePageData.CategoryName}
        />
        <CategoryTemplates
          templateList={templateList.templates}
          filters={filters}
          totalCount={templateList.total_templates}
          platform={platform}
          currerntPage={currentPage}
        />
      </div>
      {templatePageData.TemplateContent && (
        <CategoryTemplateContent
          TemplateContent={templatePageData.TemplateContent}
        />
      )}

      <Footer
        os={os}
        platform={platform}
        socialLinks={globalState.socialLinks}
        exploreLinks={footerExploreLinks[config?.env?.siteId][lang].links}
      />
    </AppLayout>
  );
}

export default TemplateCategory;

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const query = context.query;

  const templatePageData = {
    SEO: {
      Title: '5000+ Video Templates: Create Engaging Videos in Minutes',
      Description:
        "Unleash your creativity with invideo's 5000+ customizable video templates. Perfect for any type of video and easy to use, create professional videos in minutes.",
      CanonicalURL: 'https://invideo.io/templates/',
      Hreflang: [],
    },
    Head: {
      Title: 'Explore Unlimited ',
      HighlightedTitle: 'invideo Templates!',
      SubTitle:
        "Got a video to create? No worries, we've got virtually unlimited video templates to help you jazz it up! Whether it's for social media videos, YouTube videos, corporate presentations or just funny meme videos, our templates are super easy to use and fully customizable with a drag-and-drop interface. Make your videos pop!",
    },
  };

  let page: number | string | string[] | undefined = query.page;

  if (typeof page === 'string') {
    page = parseInt(page);
  } else {
    page = 1;
  }

  const templateCategories = await fetchAllTemplateCategories();

  let templateList: TemplateSearchData | null = null;

  let filters = parseFiltersFromQueryParams('https://invideo.io');

  const payload: TemplateSearchPayload = {
    count: TEMPLATE_LIMIT,
    offset: page * TEMPLATE_LIMIT,
    query: filters,
    workflow: 'marketing',
  };

  const templateListResponse = await searchTemplates(payload);

  if (templateListResponse) {
    templateList = processTemplateSearchAPIResponse(templateListResponse);
  }

  if (templateList?.templates.length === 0) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      templatePageData,
      templateList,
      filters,
      templateCategories,
      currentPage: page,
    },
  };
}
